import { Children, isValidElement, useMemo, useState, type ReactNode, type ReactElement } from 'react';
import { useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import { AppShellContext, type AppShellLayout, type AppShellContextValue, type NavbarMode, type SizingTier } from './AppShellContext.js';
import { AppShellHeader } from './AppShell.Header.js';
import { AppShellNavbar } from './AppShell.Navbar.js';
import { AppShellAside } from './AppShell.Aside.js';
import { AppShellFooter } from './AppShell.Footer.js';
import { AppShellMain } from './AppShell.Main.js';
import { AppShellSection } from './AppShell.Section.js';
import { AppShellNavLink } from './AppShell.NavLink.js';
import './AppShell.css';

export type { AppShellLayout, NavbarMode };

export interface AppShellProps {
  children?: ReactNode;
  /** `'full'` (default): edge-to-edge canvas. `'box'`: max-width centered. */
  layout?: AppShellLayout;
  /** Horizontal padding inside `<AppShell.Main>`. Default `'lg'`. */
  paddingX?: SizingTier;
  /** Vertical padding inside `<AppShell.Main>`. Default `'lg'`. */
  paddingY?: SizingTier;
  /** When `layout='box'`, the max-width of the content area. Default `'xl'`. */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  /** Header height in px. Default 60. */
  headerHeight?: number;
  /** Footer height in px. Default 0 (no footer reserved). */
  footerHeight?: number;
  /** Aside width in px. Default 320. */
  asideWidth?: number;
  /** Pixel widths the Navbar uses for each mode. */
  navbarWidth?: { icon?: number; compact?: number; full?: number };
  /** Initial navbar mode (uncontrolled). */
  defaultNavbarMode?: NavbarMode;
  /** Controlled navbar mode. */
  navbarMode?: NavbarMode;
  /** Change handler when navbar mode toggles. */
  onNavbarModeChange?: (mode: NavbarMode) => void;
  className?: string;
}

interface AppShellComposite extends React.FC<AppShellProps> {
  Header: typeof AppShellHeader;
  Navbar: typeof AppShellNavbar;
  Aside: typeof AppShellAside;
  Footer: typeof AppShellFooter;
  Main: typeof AppShellMain;
  Section: typeof AppShellSection;
  NavLink: typeof AppShellNavLink;
}

/**
 * App-shell layout: header, navbar, aside, footer, main content. CSS-grid
 * driven, fully responsive to navbar collapse modes.
 *
 *   <AppShell layout="full" paddingX="lg" defaultNavbarMode="full">
 *     <AppShell.Header>…</AppShell.Header>
 *     <AppShell.Navbar>
 *       <AppShell.NavLink label="Inbox" leftSection={<InboxIcon />} active />
 *       …
 *     </AppShell.Navbar>
 *     <AppShell.Main>
 *       <Outlet />
 *     </AppShell.Main>
 *   </AppShell>
 *
 * Layout variants:
 *   • `layout="full"`: content stretches edge-to-edge.
 *     `paddingX` controls the gutter inside `<AppShell.Main>`.
 *   • `layout="box"`  - content is a centered `max-width` box.
 *     `paddingX` still applies as the box's inner gutter; `maxWidth`
 *     constrains the box width.
 *
 * Navbar modes (`<AppShell.Navbar>` width adapts and `<AppShell.NavLink>`
 * hides label/description automatically):
 *   • `icon`: narrow rail, icon only, label rendered in tooltip.
 *   • `compact`: narrow rail, icon + tiny label below.
 *   • `full`: wide rail, icon + label (+ optional description).
 */
function AppShellRoot({
  children,
  layout = 'full',
  paddingX = 'lg',
  paddingY = 'lg',
  maxWidth = 'xl',
  headerHeight = 60,
  footerHeight = 0,
  asideWidth = 320,
  navbarWidth = {},
  defaultNavbarMode = 'full',
  navbarMode,
  onNavbarModeChange,
  className
}: AppShellProps): ReactNode {
  const [mode, setMode] = useUncontrolled<NavbarMode>({
    value: navbarMode,
    defaultValue: defaultNavbarMode,
    finalValue: 'full',
    onChange: onNavbarModeChange
  });

  const resolvedWidths = useMemo(
    () => ({
      icon: navbarWidth.icon ?? 64,
      compact: navbarWidth.compact ?? 88,
      full: navbarWidth.full ?? 280
    }),
    [navbarWidth.icon, navbarWidth.compact, navbarWidth.full]
  );

  // Detect which regions are present from the children: used to size the grid.
  let hasHeader = false, hasNavbar = false, hasAside = false, hasFooter = false;
  Children.forEach(children, (c) => {
    if (!isValidElement(c)) return;
    const name = (c.type as { displayName?: string }).displayName;
    if (name === 'AppShellHeader') hasHeader = true;
    else if (name === 'AppShellNavbar') hasNavbar = true;
    else if (name === 'AppShellAside') hasAside = true;
    else if (name === 'AppShellFooter') hasFooter = true;
  });

  const value: AppShellContextValue = useMemo(
    () => ({
      layout,
      paddingX,
      paddingY,
      maxWidth,
      navbarMode: mode,
      setNavbarMode: setMode,
      navbarWidth: resolvedWidths,
      headerHeight,
      footerHeight,
      asideWidth,
      hasNavbar,
      hasAside
    }),
    [layout, paddingX, paddingY, maxWidth, mode, setMode, resolvedWidths, headerHeight, footerHeight, asideWidth, hasNavbar, hasAside]
  );

  const currentNavbarWidth = resolvedWidths[mode];

  return (
    <AppShellContext.Provider value={value}>
      <div
        className={[
          'rr-app-shell',
          `rr-app-shell--layout-${layout}`,
          `rr-app-shell--navbar-${mode}`,
          hasHeader && 'rr-app-shell--has-header',
          hasNavbar && 'rr-app-shell--has-navbar',
          hasAside && 'rr-app-shell--has-aside',
          hasFooter && 'rr-app-shell--has-footer',
          className
        ].filter(Boolean).join(' ')}
        style={{
          // Only reserve column / row space when the corresponding child is
          // actually mounted. Inline styles beat the `:not(.--has-*)` CSS
          // rule, so unconditionally emitting these here used to leak a
          // 320px ghost aside column when no <AppShell.Aside> was present.
          ['--rr-app-shell-header' as string]: hasHeader ? `${headerHeight}px` : '0px',
          ['--rr-app-shell-footer' as string]: hasFooter ? `${footerHeight}px` : '0px',
          ['--rr-app-shell-navbar' as string]: hasNavbar ? `${currentNavbarWidth}px` : '0px',
          ['--rr-app-shell-aside' as string]: hasAside ? `${asideWidth}px` : '0px'
        } as React.CSSProperties}
      >
        {children}
      </div>
    </AppShellContext.Provider>
  );
}

export const AppShell = AppShellRoot as AppShellComposite;
AppShell.Header = AppShellHeader;
AppShell.Navbar = AppShellNavbar;
AppShell.Aside = AppShellAside;
AppShell.Footer = AppShellFooter;
AppShell.Main = AppShellMain;
AppShell.Section = AppShellSection;
AppShell.NavLink = AppShellNavLink;
